/**
 * Mentions écartées par l'utilisateur, mémorisées d'une visite à l'autre.
 *
 * Le refus d'une demande de suppression reste vrai en base : il n'y a pas de
 * bouton pour l'effacer, et il ne faut pas en créer un — l'auteur n'a pas à
 * pouvoir supprimer la trace d'une décision du service informatique. Mais il doit
 * pouvoir la ranger une fois lue, sans quoi la pastille reste sur sa liste
 * indéfiniment.
 *
 * La clé porte **l'horodatage** de la décision, et pas seulement l'identifiant de
 * l'application : un nouveau refus produit une nouvelle clé, donc réapparaît. Sans
 * cela, écarter une mention une fois masquerait toutes les suivantes, et l'auteur
 * ne verrait jamais la réponse à sa deuxième demande.
 *
 * Le stockage est passé en paramètre : c'est ce qui rend ces fonctions
 * vérifiables, et ce qui leur permet de ne rien casser quand il est indisponible
 * — navigation privée, quota atteint, réglage du navigateur.
 */

const PREFIXE = 'portail:masque:';

/** Interface minimale du stockage, pour n'en dépendre que par ce qu'on utilise. */
export interface StockageMinimal {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
}

/**
 * Clé d'une mention. `null` lorsque la mention n'a pas d'objet : rien à écarter,
 * et surtout aucune clé fourre-tout qui masquerait tout ce qui lui ressemble.
 */
export function dismissalKey(
  sujet: string,
  appId: string,
  horodatage: string | null,
): string | null {
  if (!horodatage) return null;
  return `${PREFIXE}${sujet}:${appId}:${horodatage}`;
}

/** La mention a-t-elle déjà été écartée ? */
export function isDismissed(storage: StockageMinimal | null, cle: string | null): boolean {
  if (!storage || !cle) return false;
  try {
    return storage.getItem(cle) === '1';
  } catch {
    // Un stockage inaccessible ne doit pas faire disparaître l'information : en
    // cas de doute, on la montre.
    return false;
  }
}

/** Écarte la mention. Sans effet si le stockage refuse d'écrire. */
export function dismiss(storage: StockageMinimal | null, cle: string | null): void {
  if (!storage || !cle) return;
  try {
    storage.setItem(cle, '1');
  } catch {
    // La mention sera masquée pour la session en cours, sans être retenue.
  }
}

/** Stockage du navigateur, ou `null` s'il est inaccessible. */
export function browserStorage(): StockageMinimal | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
